import type {
  DailyDia,
  DailySerie,
  GrupoMaquina,
  MaquinaDia,
  MaquinaOp,
  MaquinaSemana,
  MaquinaSerie,
  MaquinasPayload,
  SituacaoDiaMaquina,
  SituacaoOpMaquina,
  SopMes,
} from '@/types/dashboards';

/**
 * Regras de tela da aba Por máquina (MFR). A conta do MFR vem pronta do backend; aqui só
 * se recorta (mês, grupo, máquina) e se monta o que a matriz, os gráficos e a tabela desenham.
 */

export type FaixaMfr = 'bom' | 'alerta' | 'critico';

export function faixaMfr(mfr: number | null, limiares: { alerta: number; critico: number }): FaixaMfr | null {
  if (mfr === null) return null;
  if (mfr < limiares.critico) return 'critico';
  if (mfr < limiares.alerta) return 'alerta';
  return 'bom';
}

const DIA_MS = 86_400_000;
const utc = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** Todos os dias do mês de produção (16 a 15), em AAAA-MM-DD: as colunas da matriz. */
export function diasDoMes(mes: Pick<SopMes, 'start' | 'end'>): string[] {
  const dias: string[] = [];
  for (let t = utc(mes.start); t <= utc(mes.end); t += DIA_MS) dias.push(iso(t));
  return dias;
}

export interface SemanaDoMes {
  inicio: string;
  fim: string;
  dias: string[];
}

/** Semanas de domingo a sábado, cortadas no mês de produção — a mesma regra do backend. */
export function semanasDoMes(mes: Pick<SopMes, 'start' | 'end'>): SemanaDoMes[] {
  const semanas: SemanaDoMes[] = [];
  for (const dia of diasDoMes(mes)) {
    const domingo = new Date(utc(dia)).getUTCDay() === 0;
    if (domingo || semanas.length === 0) semanas.push({ inicio: dia, fim: dia, dias: [] });
    const atual = semanas[semanas.length - 1];
    atual.dias.push(dia);
    atual.fim = dia;
  }
  return semanas;
}

export interface SeriesDoMes {
  maquinas: MaquinaSerie[];
  total: MaquinaSerie | null;
}

export function seriesDoMes(p: MaquinasPayload, mes: string, grupo: GrupoMaquina): SeriesDoMes {
  return {
    maquinas: p.maquinas.filter((s) => s.mes === mes && s.grupo === grupo),
    total: p.totais.find((s) => s.mes === mes && s.grupo === grupo) ?? null,
  };
}

export const diasPorData = (serie: MaquinaSerie): Map<string, MaquinaDia> => new Map(serie.dias.map((d) => [d.data, d]));

export const semanaPorInicio = (serie: MaquinaSerie): Map<string, MaquinaSemana> =>
  new Map(serie.semanas.map((s) => [s.inicio, s]));

/** A última semana que já tem dia fechado: é o "WTD" da reunião. */
export function ultimaSemanaComMfr(serie: MaquinaSerie | null): MaquinaSemana | null {
  const comDado = (serie?.semanas ?? []).filter((s) => s.dias_fechados > 0 && s.mfr !== null);
  return comDado[comDado.length - 1] ?? null;
}

/** A máquina com o menor MFR no mês (só as que têm dia fechado). */
export function piorMaquina(maquinas: MaquinaSerie[]): MaquinaSerie | null {
  const comMfr = maquinas.filter((s) => s.mes_total.mfr !== null);
  if (comMfr.length === 0) return null;
  return comMfr.reduce((pior, s) => ((s.mes_total.mfr as number) < (pior.mes_total.mfr as number) ? s : pior));
}

export interface PontoLinhaDoTempo {
  data: string;
  programado: number;
  produzido: number;
  situacao: SituacaoDiaMaquina | 'sem_op';
  mfr: number | null;
  ops: number;
}

/** Um ponto por dia do mês (dia sem OP entra zerado, para o eixo não pular dias). */
export function linhaDoTempo(serie: MaquinaSerie, mes: Pick<SopMes, 'start' | 'end'>): PontoLinhaDoTempo[] {
  const porData = diasPorData(serie);
  return diasDoMes(mes).map((data) => {
    const d = porData.get(data);
    return d
      ? { data, programado: d.programado, produzido: d.produzido, situacao: d.situacao, mfr: d.mfr, ops: d.ops }
      : { data, programado: 0, produzido: 0, situacao: 'sem_op', mfr: null, ops: 0 };
  });
}

// --------------------------------------------------------------------------- OPs

export type FiltroOps = 'todas' | 'sem_apontamento' | 'encerradas' | 'abertas' | 'produziu';

export const FILTROS_OPS: { id: FiltroOps; rotulo: string; situacoes: SituacaoOpMaquina[] | null }[] = [
  { id: 'todas', rotulo: 'Todas', situacoes: null },
  { id: 'sem_apontamento', rotulo: 'Sem apontamento', situacoes: ['iniciada_sem_apontamento', 'nao_iniciada'] },
  { id: 'encerradas', rotulo: 'Encerradas sem produzir', situacoes: ['encerrada_sem_produzir'] },
  { id: 'abertas', rotulo: 'Em aberto e programadas', situacoes: ['em_aberto', 'futura'] },
  { id: 'produziu', rotulo: 'Produziram', situacoes: ['produziu'] },
];

/**
 * Situação da OP. Snapshot gerado antes de 14/10/2026 (régua do pallet) traz `iniciada_sem_pallet`:
 * vale como `iniciada_sem_apontamento` até a próxima atualização, em vez de quebrar a aba.
 */
export const situacaoDaOp = (o: MaquinaOp): SituacaoOpMaquina =>
  (o.situacao as string) === 'iniciada_sem_pallet' ? 'iniciada_sem_apontamento' : o.situacao;

export const opsDoMes = (p: MaquinasPayload, mes: string, grupo: GrupoMaquina): MaquinaOp[] =>
  p.ops.filter((o) => o.mes === mes && o.grupo === grupo);

export interface ResumoSituacao {
  ops: number;
  programado: number;
}

export function resumoOps(ops: MaquinaOp[]): Record<SituacaoOpMaquina, ResumoSituacao> {
  const vazio = (): ResumoSituacao => ({ ops: 0, programado: 0 });
  const r: Record<SituacaoOpMaquina, ResumoSituacao> = {
    produziu: vazio(),
    em_aberto: vazio(),
    futura: vazio(),
    nao_iniciada: vazio(),
    iniciada_sem_apontamento: vazio(),
    encerrada_sem_produzir: vazio(),
  };
  for (const o of ops) {
    const situacao = situacaoDaOp(o);
    r[situacao].ops += 1;
    r[situacao].programado += o.programado;
  }
  return r;
}

export function contarFiltro(ops: MaquinaOp[], filtro: FiltroOps): number {
  const situacoes = FILTROS_OPS.find((f) => f.id === filtro)?.situacoes ?? null;
  return situacoes ? ops.filter((o) => situacoes.includes(situacaoDaOp(o))).length : ops.length;
}

const futura = (o: MaquinaOp) => o.situacao === 'futura';

/** Ordem da tabela: o que já aconteceu, do mais novo para o mais antigo; depois as OPs por começar. */
function porData(a: MaquinaOp, b: MaquinaOp): number {
  const grupo = Number(futura(a)) - Number(futura(b));
  if (grupo !== 0) return grupo;
  return futura(a) ? a.data.localeCompare(b.data) : b.data.localeCompare(a.data);
}

/** OPs do filtro: a reunião olha os últimos dias primeiro; as programadas vêm no fim. */
export function filtrarOps(ops: MaquinaOp[], filtro: FiltroOps, maquina: string): MaquinaOp[] {
  const situacoes = FILTROS_OPS.find((f) => f.id === filtro)?.situacoes ?? null;
  return ops
    .filter((o) => (!situacoes || situacoes.includes(situacaoDaOp(o))) && (!maquina || o.maquina === maquina))
    .sort((a, b) => porData(a, b) || a.maquina.localeCompare(b.maquina) || a.op - b.op);
}

/** Produzido ÷ programado da OP, em %; `null` sem programado ou com a OP ainda por começar. */
export const atendimento = (o: Pick<MaquinaOp, 'programado' | 'produzido' | 'situacao'>): number | null =>
  o.programado > 0 && o.situacao !== 'futura' ? (o.produzido / o.programado) * 100 : null;

// --------------------------------------------------------------------------- daily por linha

/** As linhas da reunião diária no mês, na ordem do backend (sopro consolidado, 100, 250, GV, PV). */
export const dailyDoMes = (p: MaquinasPayload, mes: string): DailySerie[] => (p.daily ?? []).filter((d) => d.mes === mes);

export interface PontoDaily {
  data: string;
  programado: number;
  produzido: number;
  /** Do dia na linha; dia sem OP nem meta: fechado até o corte, futuro depois. */
  situacao: SituacaoDiaMaquina;
  /** OPs do dia ainda não encerradas. */
  abertas: number;
  /** Programação acumulada (a meta ou as OPs), dia a dia. */
  programacaoAcum: number;
  /** Produzido acumulado enquanto todos os dias até ali estão fechados; depois, `null`. */
  realizadoAcum: number | null;
  /** Do último dia fechado em diante: o realizado + o produzido dos dias fechados e o programado dos abertos. */
  projecaoAcum: number | null;
}

const situacaoDoDia = (d: DailyDia | undefined, data: string, corte: string): SituacaoDiaMaquina =>
  d?.situacao ?? (data > corte ? 'futuro' : 'fechado');

/** O que o dia soma à projeção (Attila, 16/10/2026): fechado entra pelo realizado, aberto pelo programado. */
const somaDaProjecao = (p: Pick<PontoDaily, 'situacao' | 'programado' | 'produzido'>): number =>
  p.situacao === 'fechado' ? p.produzido : p.programado;

/**
 * Um ponto por dia do mês. O dia fecha quando as ordens dele fecham, então pode haver dia aberto
 * entre dias fechados. O produzido acumulado vai até o último dia antes do primeiro aberto; dali a
 * projeção soma, dia a dia, o realizado dos fechados e o programado dos abertos — no fim do mês dá a
 * `projecao` do backend. Mês todo fechado não tem projeção (o realizado é o número).
 */
export function pontosDaily(serie: DailySerie, mes: Pick<SopMes, 'start' | 'end'>, corte: string): PontoDaily[] {
  const porData = new Map(serie.dias.map((d) => [d.data, d]));
  const base = diasDoMes(mes).map((data) => {
    const d = porData.get(data);
    return { data, programado: d?.programado ?? 0, produzido: d?.produzido ?? 0, abertas: d?.abertas ?? 0, situacao: situacaoDoDia(d, data, corte) };
  });
  const primeiroAberto = base.findIndex((p) => p.situacao !== 'fechado');
  const fimDoRealizado = primeiroAberto === -1 ? base.length - 1 : primeiroAberto - 1;
  let programacao = 0;
  let realizado = 0;
  let projecao = 0;
  return base.map((p, i) => {
    programacao += p.programado;
    if (i <= fimDoRealizado) realizado += p.produzido;
    if (i === fimDoRealizado) projecao = realizado;
    if (i > fimDoRealizado) projecao += somaDaProjecao(p);
    const temProjecao = primeiroAberto !== -1 && i >= fimDoRealizado;
    return {
      ...p,
      programacaoAcum: programacao,
      realizadoAcum: i <= fimDoRealizado ? realizado : null,
      projecaoAcum: temProjecao ? projecao : null,
    };
  });
}

/** O mês ainda tem dia por fechar: algum dia aberto na daily ou o fim do mês depois do corte. */
export const mesDailyAberto = (linhas: DailySerie[], mes: Pick<SopMes, 'end'>, corte: string): boolean =>
  mes.end > corte || linhas.some((l) => l.dias.some((d) => d.situacao !== 'fechado'));
